/**
 * Single Firebase app + auth instance for the whole frontend.
 *
 * Centralizing init is what lets us call connectAuthEmulator() exactly once — it
 * throws if called twice on the same auth instance, or after the instance has
 * been used. Previously three modules each called initializeApp(); they now all
 * import `auth` from here.
 *
 * Local / E2E testing: set REACT_APP_FIREBASE_AUTH_EMULATOR_HOST=localhost:9099
 * to route Firebase Auth at the emulator. Unset in normal/prod builds → real Auth.
 */
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { FIREBASE_CONFIG } from './config/api';

const app = getApps().length ? getApp() : initializeApp(FIREBASE_CONFIG);

export const auth = getAuth(app);

const emulatorHost = process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST;
if (emulatorHost && process.env.NODE_ENV !== 'production') {
  // Only ever point the Auth client (which carries passwords) at a loopback host —
  // mirrors the backend emulatorMode guard so a non-local value can't exfiltrate.
  const hostname = emulatorHost.replace(/^https?:\/\//, '').replace(/^\[/, '').split(/[\]:]/)[0];
  if (['localhost', '127.0.0.1', '::1'].includes(hostname)) {
    const url = /^https?:\/\//.test(emulatorHost) ? emulatorHost : `http://${emulatorHost}`;
    connectAuthEmulator(auth, url, { disableWarnings: true });
    // eslint-disable-next-line no-console
    console.info(`[firebase] Auth emulator connected: ${url}`);
  } else {
    // eslint-disable-next-line no-console
    console.warn(`[firebase] ignoring non-loopback emulator host: ${emulatorHost}`);
  }
}

export default app;
